"use client";

import Image from "@/components/shared/image";
import { useLayoutEffect, useRef } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import ArrowUpRight01Icon from "@hugeicons/core-free-icons/ArrowUpRight01Icon";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  getHomepageMotionEnvironment,
  shouldUseHeavyHomepageMotion,
} from "@/components/LandingPage/mobile-motion-policy";

const PARTNER_STRIP_LOGOS = [
  { src: "/microsoft-for-startup.png", alt: "Microsoft for Startups" },
  { src: "/stripe-startup-partner-program.png", alt: "Stripe Startup Partner Program" },
  { src: "/meta-verified_sq.png", alt: "Meta Tech Provider" },
] as const;

const highlights = [
  "Meta approved Tech Provider & Business Partner",
  "Official Instagram APIs and platform policies",
  "API-limit safeguards for safer automation",
  "Secure OAuth for Business & Creator accounts",
];

/** Soft lift shadow biased toward the bottom (no harsh all-around card shadow). */
const CARD_BOTTOM_SHADOW =
  "0 22px 56px -14px rgba(2, 6, 18, 0.42), 0 10px 24px -12px rgba(15, 23, 42, 0.18)";

export default function SafeAndComplient() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const stackBackRef = useRef<HTMLDivElement | null>(null);
  const stackMidRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const outerTailRef = useRef<HTMLDivElement | null>(null);
  const middleTailRef = useRef<HTMLDivElement | null>(null);
  const outerDotRef = useRef<HTMLDivElement | null>(null);
  const middleDotRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const useHeavyMotion = shouldUseHeavyHomepageMotion(getHomepageMotionEnvironment());
    if (reduced || !useHeavyMotion) return;

    gsap.registerPlugin(ScrollTrigger);
    const scrollScrubTweens: gsap.core.Tween[] = [];

    const ctx = gsap.context(() => {
      const back = stackBackRef.current;
      const mid = stackMidRef.current;
      const card = cardRef.current;

      gsap.set([back, mid, card].filter(Boolean), {
        opacity: 0,
        y: 36,
        scale: 0.985,
        transformOrigin: "50% 100%",
      });

      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
        scrollTrigger: {
          trigger: section,
          start: "top 86%",
          once: true,
        },
      });

      tl.to(back, { opacity: 1, y: 0, scale: 1, duration: 0.72 })
        .to(mid, { opacity: 1, y: 0, scale: 1, duration: 0.72 }, "-=0.52")
        .to(card, { opacity: 1, y: 0, scale: 1, duration: 0.88 }, "-=0.48");

      tl.eventCallback("onComplete", () => {
        if (!back || !mid) return;
        // Skip scrub on touch devices — per-frame scaleX repaints cause jank on mobile
        if (window.matchMedia("(pointer: coarse)").matches) return;
        scrollScrubTweens.push(
          gsap.fromTo(
            back,
            { scaleX: 0.9 },
            {
              scaleX: 1.08,
              ease: "none",
              scrollTrigger: {
                trigger: section,
                start: "top bottom",
                end: "bottom top",
                scrub: 0.55,
              },
            },
          ),
          gsap.fromTo(
            mid,
            { scaleX: 0.94 },
            {
              scaleX: 1.05,
              ease: "none",
              scrollTrigger: {
                trigger: section,
                start: "top bottom",
                end: "bottom top",
                scrub: 0.55,
              },
            },
          ),
        );
      });

      const outerTailTween = gsap.to(outerTailRef.current, {
        rotation: 360,
        duration: 40,
        ease: "none",
        repeat: -1,
        transformOrigin: "50% 50%",
        paused: true,
      });

      const middleTailTween = gsap.to(middleTailRef.current, {
        rotation: -360,
        duration: 28,
        ease: "none",
        repeat: -1,
        transformOrigin: "50% 50%",
        paused: true,
      });

      const outerDotTween = gsap.to(outerDotRef.current, {
        opacity: 0.45,
        scale: 0.82,
        duration: 0.85,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        paused: true,
      });

      const middleDotTween = gsap.to(middleDotRef.current, {
        opacity: 0.5,
        scale: 0.84,
        duration: 0.72,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        paused: true,
      });

      // Only run infinite tweens when section is visible — saves GPU on scroll
      ScrollTrigger.create({
        trigger: section,
        start: "top bottom",
        end: "bottom top",
        onEnter:     () => { outerTailTween.play(); middleTailTween.play(); outerDotTween.play(); middleDotTween.play(); },
        onLeave:     () => { outerTailTween.pause(); middleTailTween.pause(); outerDotTween.pause(); middleDotTween.pause(); },
        onEnterBack: () => { outerTailTween.play(); middleTailTween.play(); outerDotTween.play(); middleDotTween.play(); },
        onLeaveBack: () => { outerTailTween.pause(); middleTailTween.pause(); outerDotTween.pause(); middleDotTween.pause(); },
      });
    }, section);

    return () => {
      scrollScrubTweens.forEach((tween) => tween.kill());
      ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="w-full border-t border-border/40 bg-background">
      <div className="mx-auto w-full max-w-7xl px-2 pt-12 pb-5 sm:container-padding sm:pt-14 sm:pb-6">
        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center">
          {/* Stack: small (back) → medium → large (front, interactive) */}
          <div
            ref={stackBackRef}
            aria-hidden
            className="relative z-0 w-[min(82%,40rem)] shrink-0 rounded-[1.25rem] border border-zinc-800/35 bg-gradient-to-br from-[#03050c] via-[#04040f] to-[#050812] min-h-[2.75rem] sm:min-h-[3.25rem] sm:w-[min(80%,46rem)] sm:rounded-[1.35rem] -mb-[2.1rem] sm:-mb-[2.35rem] [will-change:transform]"
          />
          <div
            ref={stackMidRef}
            aria-hidden
            className="relative z-[1] w-[min(94%,54rem)] shrink-0 rounded-[1.65rem] border border-zinc-800/42 bg-gradient-to-br from-[#04060f] via-[#050113] to-[#060a16] min-h-[3.25rem] sm:min-h-[3.75rem] sm:w-[min(92%,60rem)] sm:rounded-[1.85rem] -mb-[2.35rem] sm:-mb-[2.65rem] [will-change:transform]"
          />
          <div
            ref={cardRef}
            className="relative z-[2] w-full overflow-hidden rounded-[2.5rem] border border-zinc-800/50 bg-gradient-to-br from-[#050816] via-[#050113] to-[#090016] px-5 py-6 text-white sm:px-8 sm:py-7"
            style={{ boxShadow: CARD_BOTTOM_SHADOW }}
          >
            {/* Circular rings + conic accents (same language as CTA) */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.55] dark:opacity-70">
              <div className="relative h-[min(95vw,520px)] w-[min(95vw,520px)] shrink-0 rounded-full border border-sky-300/10 sm:h-[min(85vw,580px)] sm:w-[min(85vw,580px)]">
                <div className="absolute inset-[12.5%] rounded-full border border-fuchsia-300/12" />
                <div className="absolute inset-[25%] rounded-full border border-cyan-300/16" />

                <div ref={outerTailRef} className="absolute inset-0">
                  <div className="absolute inset-0 rounded-full [mask:radial-gradient(farthest-side,transparent_calc(100%-1.5px),#000_calc(100%-1.5px))] [background:conic-gradient(from_-82deg,transparent_0deg,transparent_14deg,rgba(103,232,249,0.10)_20deg,rgba(103,232,249,0.38)_27deg,rgba(125,211,252,0.75)_35deg,transparent_44deg,transparent_360deg)]" />
                  <div className="absolute left-1/2 top-0 h-8 w-8 -translate-x-[34%] -translate-y-1/2">
                    <div
                      ref={outerDotRef}
                      className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-gradient-to-br from-white via-sky-200 to-cyan-400 shadow-[0_0_18px_rgba(56,189,248,0.75)]"
                    />
                  </div>
                </div>

                <div ref={middleTailRef} className="absolute inset-[12.5%] rounded-full">
                  <div className="absolute inset-0 rounded-full [mask:radial-gradient(farthest-side,transparent_calc(100%-1.2px),#000_calc(100%-1.2px))] [background:conic-gradient(from_-98deg,transparent_0deg,transparent_315deg,rgba(244,114,182,0.85)_324deg,rgba(236,72,153,0.45)_333deg,rgba(244,114,182,0.12)_340deg,transparent_348deg,transparent_360deg)]" />
                  <div className="absolute left-1/2 top-0 h-7 w-7 -translate-x-[-6%] -translate-y-1/2">
                    <div
                      ref={middleDotRef}
                      className="absolute left-0 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-gradient-to-br from-white via-fuchsia-200 to-purple-400 shadow-[0_0_14px_rgba(232,121,249,0.8)]"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pointer-events-none absolute -left-24 -top-20 h-56 w-56 rounded-full bg-purple-500/20 blur-3xl sm:-left-32 sm:-top-28 sm:h-72 sm:w-72 sm:bg-purple-500/25" />
            <div className="pointer-events-none absolute -right-20 bottom-[-40px] h-52 w-52 rounded-full bg-sky-500/15 blur-3xl sm:-right-28 sm:bottom-[-52px] sm:h-64 sm:w-64 sm:bg-sky-500/20" />

            <div className="relative z-10 grid items-center gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,400px)] lg:gap-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300/90">Safe & Compliant</p>
                <h2 className="mt-2 max-w-xl text-balance text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                  Official Meta APIs. No bots. No password sharing.
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/70 sm:text-base">
                  Automate Instagram conversations through secure OAuth and policy-aware platform connections.
                </p>

                <ul className="mt-4 space-y-2 sm:mt-5 sm:space-y-2.5">
                  {highlights.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-white/85 sm:text-base">
                      <span className="mt-0.5 shrink-0 text-sky-300" aria-hidden>
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative mx-auto w-full max-w-md lg:max-w-none">
                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.06] p-3 backdrop-blur-[2px] sm:p-4">
                  <Image
                    src="/meta-provider.webp"
                    alt="Meta Tech Provider"
                    width={900}
                    height={600}
                    className="mx-auto h-auto w-[92%] rounded-xl object-contain sm:w-full"
                    priority={false}
                  />

                  <div className="mx-auto mt-4 grid w-full grid-cols-3 gap-2 border-t border-white/10 pt-4 sm:mt-5 sm:gap-3 sm:pt-5">
                    {PARTNER_STRIP_LOGOS.map((logo) => (
                      <div
                        key={logo.src}
                        className="relative h-[4.75rem] w-full min-w-0 overflow-hidden rounded-lg border border-white/10 bg-white/[0.07] sm:h-[5.5rem]"
                      >
                        <Image
                          src={logo.src}
                          alt={logo.alt}
                          fill
                          sizes="(max-width: 640px) 30vw, 220px"
                          className="object-contain p-2 sm:p-2.5"
                          priority={false}
                        />
                      </div>
                    ))}
                  </div>

                  <a
                    href="https://zapify.pro/verified"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-4 flex w-full items-center justify-center sm:mt-5"
                  >
                    <span className="inline-flex w-full max-w-sm items-center justify-between gap-3 rounded-full border border-sky-400/25 bg-gradient-to-r from-sky-500/15 via-white/[0.06] to-cyan-500/10 px-4 py-2.5 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] transition-[border-color,background-color,transform] hover:border-sky-300/45 hover:from-sky-500/25 hover:to-cyan-500/15 sm:max-w-md sm:px-5 sm:py-3 sm:text-sm">
                      <span className="tracking-wide">Know More</span>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 text-sky-200 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:border-sky-300/40 group-hover:bg-sky-400/20 group-hover:text-white sm:size-9">
                        <HugeiconsIcon
                          icon={ArrowUpRight01Icon}
                          size={19}
                          color="currentColor"
                          aria-hidden
                        />
                      </span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
